/**
 * What a player carries: the resources and the blocks, as the rules keep them per player, and what
 * they can spend (without end in admin mode, rules 26). Pure selectors over a state, for the HUD
 * and the panels; the rules change the pockets themselves.
 */
import {
  canAfford,
  emptyPockets,
  isAdmin,
  ITEMS,
  missing,
  type Cost,
  type CrewState,
  type Item,
  type Pockets,
} from '@bramvasterd/terrella-rules';

export const EMPTY_POCKETS: Readonly<Pockets> = Object.freeze(emptyPockets());

/** A player's pockets, empty for one who never acted. */
export function pocketsOf(state: CrewState | null, player: string | null): Readonly<Pockets> {
  if (!state || player === null) return EMPTY_POCKETS;
  return state.players.get(player)?.pockets ?? EMPTY_POCKETS;
}

/** Every item without end: what an admin has to spend. */
const UNLIMITED: Readonly<Pockets> = Object.freeze(
  Object.fromEntries(ITEMS.map((item) => [item, Infinity])) as unknown as Pockets,
);

/** Whether a player is in admin mode (rules 26): nothing costs them anything, their pockets have no end. */
export const adminOf = (state: CrewState | null, player: string | null): boolean =>
  state !== null && player !== null && isAdmin(state, player);

/** What a player can spend: their pockets, or every item without end in admin mode. */
export const spendableOf = (state: CrewState | null, player: string | null): Readonly<Pockets> =>
  adminOf(state, player) ? UNLIMITED : pocketsOf(state, player);

/** The items of a cost that are more than zero, in the fixed order: the resources, then the blocks. */
export function costEntries(cost: Cost): [Item, number][] {
  const out: [Item, number][] = [];
  for (const resource of ITEMS) {
    const amount = cost[resource] ?? 0;
    if (amount > 0) out.push([resource, amount]);
  }
  return out;
}

export const totalOf = (cost: Cost): number =>
  ITEMS.reduce((sum, resource) => sum + (cost[resource] ?? 0), 0);

/** How a cost compares to what a player carries. */
export interface Affordance {
  cost: Cost;
  have: Readonly<Pockets>;
  missing: Cost;
  affordable: boolean;
  /** 0 to 1: how much of the cost is in the pockets. 1 when affordable or when nothing is needed. */
  progress: number;
}

export function affordance(have: Readonly<Pockets>, cost: Cost): Affordance {
  const need = totalOf(cost);
  const held = ITEMS.reduce(
    (sum, resource) => sum + Math.min(have[resource], cost[resource] ?? 0),
    0,
  );
  return {
    cost,
    have,
    missing: missing(have, cost),
    affordable: canAfford(have, cost),
    progress: need === 0 ? 1 : held / need,
  };
}
